import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { LogOut, RefreshCw, ArrowLeft } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { chatStorage } from '@/lib/chatStorage';

interface AuthEscapeActionsProps {
  /** Optional retry handler — if provided, shows a "Retry" button. */
  onRetry?: () => void;
  /** Optional skip handler — if provided, shows a "Skip" button with custom label. */
  onSkip?: () => void;
  skipLabel?: string;
  /** Hide the "back to home" button (defaults to false). */
  hideBack?: boolean;
  className?: string;
}

/**
 * Universal escape hatch shown on any full-screen loading / trap state.
 * Always offers a guaranteed sign-out path — never depends on backend
 * cleanup completing successfully.
 */
export function AuthEscapeActions({
  onRetry,
  onSkip,
  skipLabel = 'Skip',
  hideBack = false,
  className,
}: AuthEscapeActionsProps) {
  const navigate = useNavigate();

  const forceLogout = async () => {
    // Clear ALL local app caches first so we never come back into a stale state.
    try {
      const keys = Object.keys(localStorage);
      keys.forEach((k) => {
        if (
          k === 'verithos_active_project' ||
          k === 'verithos_focus_unlocked' ||
          k.startsWith('verithos_projects_cache_') ||
          k.startsWith('verithos_chat_')
        ) {
          localStorage.removeItem(k);
        }
      });
      sessionStorage.clear();
      chatStorage.clearAll();
    } catch {
      /* best-effort */
    }
    // Sign out from Supabase, but never block the UI on it.
    supabase.auth.signOut().catch(() => {});
    // Hard-navigate so React state is reset cleanly.
    window.location.href = '/auth?logout=1';
  };

  return (
    <div className={`flex flex-col items-center gap-2 ${className ?? ''}`}>
      <div className="flex flex-wrap items-center justify-center gap-2">
        {onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry} className="gap-1.5">
            <RefreshCw className="h-3.5 w-3.5" />
            Retry
          </Button>
        )}
        {onSkip && (
          <Button variant="outline" size="sm" onClick={onSkip}>
            {skipLabel}
          </Button>
        )}
        {!hideBack && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/', { replace: true })}
            className="gap-1.5"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Home
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={forceLogout} className="gap-1.5">
          <LogOut className="h-3.5 w-3.5" />
          Sign out &amp; switch account
        </Button>
      </div>
    </div>
  );
}
